import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import apiClient from '../api/client';
import {
  BookOpenIcon,
  HeartIcon as HeartOutline,
  UserIcon,
  LockClosedIcon,
  LockOpenIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import { FireLoader } from '../components/Loader';
import {
  getBookFile,
  getCachedBooks,
  isBookDownloaded,
} from '../utils/storage';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const API_BASE_URL = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/v1$/, '')
  : 'http://localhost:3000';
const FAVORITES_KEY = 'favorites';
const PDF_WORKER_URL =
  'https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js';
const BASE64_CHUNK_SIZE = 512 * 1024;
const FAVORITE_REQUEST_BATCH_SIZE = 8;

const getAccessToken = () =>
  localStorage.getItem('accessToken') ||
  localStorage.getItem('token') ||
  null;

const readFavorites = () => {
  try {
    const stored = localStorage.getItem(FAVORITES_KEY);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const normalizeBook = (book) => ({
  ...book,
  cover: book.cover
    ? book.cover
    : book.cover_image
      ? `${API_BASE_URL}/covers/${book.cover_image
          .split('/')
          .pop()}`
      : null,
});

const createAbortError = () => {
  const error = new Error('Opening cancelled');
  error.name = 'AbortError';
  return error;
};

const yieldToBrowser = () =>
  new Promise((resolve) => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => resolve());
    } else {
      setTimeout(resolve, 0);
    }
  });

const base64ToPdfBlob = async (
  storedValue,
  onProgress,
  isCancelled
) => {
  const value = String(storedValue || '');
  const commaIndex = value.indexOf(',');
  const base64 = (commaIndex >= 0
    ? value.slice(commaIndex + 1)
    : value
  ).replace(/\s/g, '');

  if (!base64) throw new Error('The downloaded PDF is empty.');

  const byteChunks = [];

  for (
    let offset = 0;
    offset < base64.length;
    offset += BASE64_CHUNK_SIZE
  ) {
    if (isCancelled()) throw createAbortError();

    const binary = atob(
      base64.slice(offset, offset + BASE64_CHUNK_SIZE)
    );
    const bytes = new Uint8Array(binary.length);

    for (let index = 0; index < binary.length; index += 1) {
      bytes[index] = binary.charCodeAt(index);
    }

    byteChunks.push(bytes);
    const processed = Math.min(
      offset + BASE64_CHUNK_SIZE,
      base64.length
    );
    onProgress(
      Math.min(92, Math.max(5, Math.round((processed / base64.length) * 92)))
    );

    if (processed < base64.length) await yieldToBrowser();
  }

  if (isCancelled()) throw createAbortError();

  const blob = new Blob(byteChunks, { type: 'application/pdf' });
  if (blob.size === 0) throw new Error('The PDF file is empty.');
  return blob;
};

export default function Favorites() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [favorites, setFavorites] = useState(readFavorites);
  const [purchasedIds, setPurchasedIds] = useState([]);
  const [purchasedLoading, setPurchasedLoading] = useState(true);
  const [selectedBook, setSelectedBook] = useState(null);
  const [lockedBook, setLockedBook] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfProgress, setPdfProgress] = useState(0);
  const [pdfError, setPdfError] = useState('');

  const blobUrlRef = useRef(null);
  const pdfAbortControllerRef = useRef(null);
  const openRequestIdRef = useRef(0);
  const favoriteLoadRequestIdRef = useRef(0);
  const mountedRef = useRef(true);

  // The PDF viewer plugin uses hooks internally. Call it unconditionally on
  // every render; wrapping it in useMemo breaks React hook ordering.
  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    renderToolbar: (Toolbar) => (props) => (
      <Toolbar {...props}>
        {(slots) => {
          const visibleSlots = Object.entries(slots).filter(
            ([name]) => name !== 'download' && name !== 'print'
          );

          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}
            >
              {visibleSlots.map(([name, slot]) => (
                <React.Fragment key={name}>{slot}</React.Fragment>
              ))}
            </div>
          );
        }}
      </Toolbar>
    ),
  });

  const favoriteIdSet = useMemo(
    () => new Set(favorites.map((id) => String(id))),
    [favorites]
  );
  const purchasedIdSet = useMemo(
    () => new Set(purchasedIds.map((id) => String(id))),
    [purchasedIds]
  );
  const favoriteKey = useMemo(
    () =>
      [...favoriteIdSet]
        .sort((first, second) => first.localeCompare(second))
        .join(','),
    [favoriteIdSet]
  );
  const visibleBooks = useMemo(
    () =>
      books.filter((book) =>
        favoriteIdSet.has(String(book.id))
      ),
    [books, favoriteIdSet]
  );

  const loadFavoriteBooks = useCallback(async () => {
    const requestId = favoriteLoadRequestIdRef.current + 1;
    favoriteLoadRequestIdRef.current = requestId;
    const canUpdate = () =>
      mountedRef.current &&
      requestId === favoriteLoadRequestIdRef.current;
    const favoriteIds = favoriteKey
      ? favoriteKey.split(',').filter(Boolean)
      : [];

    if (favoriteIds.length === 0) {
      setBooks([]);
      setError('');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError('');

    let cachedBooks = [];
    try {
      const cached = await getCachedBooks();
      cachedBooks = Array.isArray(cached) ? cached : [];
    } catch (cacheError) {
      console.warn('Favorite cache lookup failed:', cacheError);
    }

    const bookMap = new Map();
    cachedBooks.forEach((book) => {
      if (favoriteIdSet.has(String(book.id))) {
        bookMap.set(String(book.id), normalizeBook(book));
      }
    });

    if (canUpdate() && bookMap.size > 0) {
      setBooks(
        favoriteIds
          .map((id) => bookMap.get(id))
          .filter(Boolean)
      );
    }

    const token = getAccessToken();
    if (!token || !navigator.onLine) {
      if (canUpdate()) {
        if (bookMap.size === 0) {
          setError(
            token
              ? 'Connect to the internet to load these favorites.'
              : 'Please log in again to load your favorites.'
          );
        }
        setLoading(false);
      }
      return;
    }

    try {
      for (
        let offset = 0;
        offset < favoriteIds.length;
        offset += FAVORITE_REQUEST_BATCH_SIZE
      ) {
        const batch = favoriteIds.slice(
          offset,
          offset + FAVORITE_REQUEST_BATCH_SIZE
        );
        const results = await Promise.allSettled(
          batch.map((bookId) =>
            apiClient.get(`/books/${bookId}`, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
          )
        );

        results.forEach((result) => {
          if (result.status !== 'fulfilled') return;
          const payload = result.value.data;
          const book =
            payload?.data || payload?.result || payload?.book;
          if (book?.id) {
            bookMap.set(String(book.id), normalizeBook(book));
          }
        });

        if (canUpdate()) {
          setBooks(
            favoriteIds
              .map((id) => bookMap.get(id))
              .filter(Boolean)
          );
        }
      }

      if (canUpdate() && bookMap.size === 0) {
        setError('No favorite book details could be loaded.');
      }
    } catch (requestError) {
      console.error('Failed to fetch favorite books:', requestError);
      if (canUpdate() && bookMap.size === 0) {
        setError('Unable to load your favorite books.');
      }
    } finally {
      if (canUpdate()) setLoading(false);
    }
  }, [favoriteIdSet, favoriteKey]);

  useEffect(() => {
    mountedRef.current = true;
    loadFavoriteBooks();

    return () => {
      mountedRef.current = false;
      favoriteLoadRequestIdRef.current += 1;
    };
  }, [loadFavoriteBooks]);

  useEffect(() => {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
    } catch (_) {
      // Favorites still work for the current session.
    }
  }, [favorites]);

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === FAVORITES_KEY) {
        setFavorites(readFavorites());
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchPurchasedBooks = async () => {
      const token = getAccessToken();
      setPurchasedLoading(true);

      if (!token || !navigator.onLine) {
        if (!cancelled) setPurchasedLoading(false);
        return;
      }

      try {
        const response = await apiClient.get('/books/purchased', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const purchased = Array.isArray(response.data?.data)
          ? response.data.data
          : [];
        if (mountedRef.current && !cancelled) {
          setPurchasedIds(purchased.map((book) => book.id));
        }
      } catch (purchaseError) {
        console.debug(
          'Purchased favorite lookup skipped:',
          purchaseError.message
        );
      } finally {
        if (mountedRef.current && !cancelled) {
          setPurchasedLoading(false);
        }
      }
    };

    fetchPurchasedBooks();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!selectedBook) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        openRequestIdRef.current += 1;
        pdfAbortControllerRef.current?.abort();
        pdfAbortControllerRef.current = null;

        if (blobUrlRef.current) {
          URL.revokeObjectURL(blobUrlRef.current);
          blobUrlRef.current = null;
        }

        setSelectedBook(null);
        setPdfLoading(false);
        setPdfError('');
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedBook]);

  useEffect(
    () => () => {
      mountedRef.current = false;
      openRequestIdRef.current += 1;
      pdfAbortControllerRef.current?.abort();
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    },
    []
  );

  const toggleFavorite = useCallback((bookId, event) => {
    event?.stopPropagation();
    const bookKey = String(bookId);

    setFavorites((previous) => {
      const exists = previous.some(
        (id) => String(id) === bookKey
      );
      return exists
        ? previous.filter((id) => String(id) !== bookKey)
        : [...previous, bookId];
    });
  }, []);

  const closeReader = useCallback(() => {
    openRequestIdRef.current += 1;
    pdfAbortControllerRef.current?.abort();
    pdfAbortControllerRef.current = null;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    setSelectedBook(null);
    setPdfLoading(false);
    setPdfProgress(0);
    setPdfError('');
  }, []);

  const openBook = useCallback(async (book) => {
    const requestId = openRequestIdRef.current + 1;
    openRequestIdRef.current = requestId;

    pdfAbortControllerRef.current?.abort();
    pdfAbortControllerRef.current = null;

    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    let downloaded = false;
    try {
      downloaded = await isBookDownloaded(book.id);
    } catch (storageError) {
      console.warn('Downloaded favorite lookup failed:', storageError);
    }

    let purchased = purchasedIdSet.has(String(book.id));

    // The purchased-books request may still be loading when a card is
    // tapped. Verify this specific premium book before showing it as locked.
    if (book.premium && !purchased && !downloaded) {
      const token = getAccessToken();

      if (token && navigator.onLine) {
        try {
          const response = await apiClient.get(
            `/books/${book.id}/purchased`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          purchased =
            response.data?.purchased === true ||
            response.data?.data?.purchased === true;

          if (purchased && mountedRef.current) {
            setPurchasedIds((previous) =>
              previous.some(
                (id) => String(id) === String(book.id)
              )
                ? previous
                : [...previous, book.id]
            );
          }
        } catch (purchaseCheckError) {
          console.debug(
            'Favorite purchase check failed:',
            purchaseCheckError.message
          );
        }
      }
    }

    if (
      book.premium &&
      !purchased &&
      !downloaded
    ) {
      setLockedBook(book);
      return;
    }

    setSelectedBook({ ...book, pdfUrl: null });
    setPdfLoading(true);
    setPdfProgress(2);
    setPdfError('');

    try {
      let blob;

      if (downloaded) {
        const storedFile = await getBookFile(book.id);
        if (!storedFile) {
          throw new Error(
            'The offline PDF was not found on this device.'
          );
        }

        blob = await base64ToPdfBlob(
          storedFile,
          (progress) => {
            if (
              mountedRef.current &&
              requestId === openRequestIdRef.current
            ) {
              setPdfProgress(progress);
            }
          },
          () => requestId !== openRequestIdRef.current
        );
      } else {
        const token = getAccessToken();
        if (!token) {
          throw new Error('Please log in again to open this book.');
        }

        const controller = new AbortController();
        pdfAbortControllerRef.current = controller;
        const baseUrl = apiClient.defaults.baseURL || '';
        const response = await fetch(
          `${baseUrl}/books/${book.id}/download?t=${Date.now()}`,
          {
            method: 'GET',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/pdf',
              'Cache-Control': 'no-cache',
              Pragma: 'no-cache',
            },
            signal: controller.signal,
          }
        );

        if (!response.ok) {
          let message = `Unable to open book (${response.status})`;
          try {
            const body = await response.json();
            message = body.message || body.error || message;
          } catch (_) {
            // The response may not be JSON.
          }
          throw new Error(message);
        }

        if (!response.body) {
          throw new Error('PDF streaming is unavailable.');
        }

        const contentLength = Number.parseInt(
          response.headers.get('content-length') || '0',
          10
        );
        const reader = response.body.getReader();
        const chunks = [];
        let loaded = 0;
        let lastProgress = 0;

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          chunks.push(value);
          loaded += value.length;

          if (contentLength > 0) {
            const progress = Math.min(
              94,
              Math.round((loaded / contentLength) * 94)
            );
            if (progress >= lastProgress + 2) {
              lastProgress = progress;
              setPdfProgress(progress);
            }
          }
        }

        blob = new Blob(chunks, { type: 'application/pdf' });
        if (blob.size === 0) {
          throw new Error('The server returned an empty PDF.');
        }
      }

      if (requestId !== openRequestIdRef.current) {
        throw createAbortError();
      }

      const url = URL.createObjectURL(blob);
      blobUrlRef.current = url;
      pdfAbortControllerRef.current = null;
      setPdfProgress(96);
      setSelectedBook({ ...book, pdfUrl: url });
    } catch (openError) {
      if (
        openError.name !== 'AbortError' &&
        mountedRef.current &&
        requestId === openRequestIdRef.current
      ) {
        console.error('Favorite PDF open error:', openError);
        setPdfLoading(false);
        setPdfError(
          openError.message || 'The PDF could not be opened.'
        );
      }
    } finally {
      if (requestId === openRequestIdRef.current) {
        pdfAbortControllerRef.current = null;
      }
    }
  }, [purchasedIdSet]);

  const handleDocumentLoad = useCallback(() => {
    setPdfProgress(100);
    requestAnimationFrame(() => setPdfLoading(false));
  }, []);

  const handleDocumentError = useCallback((renderError) => {
    console.error('Favorite PDF render error:', renderError);
    setPdfLoading(false);
    setPdfError('The PDF could not be rendered.');
  }, []);

  const showOwnershipSkeleton =
    purchasedLoading &&
    visibleBooks.some((book) => book.premium);
  const ownershipSkeletonCount = Math.min(
    8,
    Math.max(4, visibleBooks.length || 4)
  );

  if (loading && books.length === 0) {
    return <FireLoader fullScreen text="Loading favorites..." />;
  }

  return (
    <>
      <style>{`
        .favorites-page { -webkit-tap-highlight-color: transparent; }
        .favorite-book-card {
          content-visibility: auto;
          contain: layout paint style;
          contain-intrinsic-size: 285px;
          backface-visibility: hidden;
          animation: favorite-card-enter 0.44s cubic-bezier(0.22, 1, 0.36, 1) backwards;
        }
        @keyframes favorite-card-enter {
          from { opacity: 0; transform: translateY(14px) scale(0.99); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes favorite-reader-enter {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .favorite-reader-modal { animation: favorite-reader-enter 0.18s ease-out; }
        @keyframes favorite-skeleton-shimmer {
          from { background-position: 200% 0; }
          to { background-position: -200% 0; }
        }
        .favorite-skeleton-shimmer {
          background: linear-gradient(
            90deg,
            #f3f4f6 25%,
            #ffffff 50%,
            #f3f4f6 75%
          );
          background-size: 200% 100%;
          animation: favorite-skeleton-shimmer 1.3s ease-in-out infinite;
        }
        .rpv-default-layout__sidebar,
        .rpv-default-layout__toolbar-sidebar-toggle,
        [aria-label="Toggle sidebar"],
        [title="Toggle sidebar"],
        .rpv-core__download-button,
        [aria-label="Download"],
        [title="Download"],
        .rpv-core__print-button,
        [aria-label="Print"],
        [title="Print"],
        .rpv-core__menu-item[aria-label="Download"],
        .rpv-core__menu-item[aria-label="Print"] { display: none !important; }
        .rpv-default-layout__main { width: 100% !important; margin-left: 0 !important; }
        .rpv-default-layout__body { grid-template-columns: 0fr 1fr !important; }
        .rpv-default-layout__sidebar {
          width: 0 !important;
          min-width: 0 !important;
          overflow: hidden !important;
        }
        .rpv-core__viewer { width: 100% !important; height: 100% !important; }
        .rpv-core__inner-page,
        .rpv-core__canvas-layer { max-width: 100% !important; }
        .rpv-core__page-layer {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .rpv-core__canvas-layer,
        .rpv-core__canvas-layer canvas { height: auto !important; }
        .rpv-core__canvas-layer canvas {
          max-width: 100%;
          width: auto !important;
        }
        @media (prefers-reduced-motion: reduce) {
          .favorite-book-card,
          .favorite-reader-modal,
          .favorite-skeleton-shimmer { animation: none !important; }
        }
      `}</style>

      <main className="favorites-page max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-5 sm:py-7">
        <header className="flex items-center gap-3 mb-5 sm:mb-7">
          <div className="w-10 h-10 rounded-xl bg-red-50 flex items-center justify-center">
            <HeartSolid className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-primary-700">
              Your Favorites
            </h1>
            <p className="text-xs sm:text-sm text-gray-500">
              {visibleBooks.length}{' '}
              {visibleBooks.length === 1 ? 'favorite book' : 'favorite books'}
            </p>
          </div>
        </header>

        {error && (
          <div className="mb-5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 flex items-center justify-between gap-3 text-sm text-red-700">
            <span>{error}</span>
            <button
              type="button"
              onClick={loadFavoriteBooks}
              className="font-semibold hover:text-red-900"
            >
              Retry
            </button>
          </div>
        )}

        {showOwnershipSkeleton ? (
          <div
            className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5"
            role="status"
            aria-label="Checking purchased books"
            aria-busy="true"
          >
            {Array.from({ length: ownershipSkeletonCount }).map(
              (_, index) => (
                <div
                  key={`favorite-ownership-skeleton-${index}`}
                  className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm"
                >
                  <div className="favorite-skeleton-shimmer h-36 sm:h-44 md:h-48" />
                  <div className="p-3 sm:p-4 space-y-2.5">
                    <div className="favorite-skeleton-shimmer h-4 sm:h-5 rounded-md" />
                    <div className="favorite-skeleton-shimmer h-3.5 w-2/3 rounded-md" />
                    <div className="pt-1 flex items-center justify-between gap-3">
                      <div className="favorite-skeleton-shimmer h-5 w-16 rounded-md" />
                      <div className="favorite-skeleton-shimmer h-5 w-14 rounded-md" />
                    </div>
                  </div>
                </div>
              )
            )}
            <span className="sr-only">
              Checking which favorite books you own...
            </span>
          </div>
        ) : visibleBooks.length === 0 ? (
          <div className="text-center py-14 sm:py-16 bg-white border border-gray-100 rounded-2xl shadow-sm">
            <div className="w-24 h-24 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
              <HeartOutline className="w-12 h-12 text-red-300" />
            </div>
            <p className="text-gray-700 text-lg font-semibold">
              No favorite books yet
            </p>
            <p className="text-gray-400 mt-1 px-5">
              Go to the library and tap the heart on books you like.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-5">
            {visibleBooks.map((book, index) => {
              const isPurchased = purchasedIdSet.has(String(book.id));

              return (
                <article
                  key={book.id}
                  onClick={() => openBook(book)}
                  className="favorite-book-card group bg-white rounded-xl border border-gray-100 overflow-hidden hover:border-primary-200 hover:-translate-y-0.5 hover:shadow-md transform-gpu transition-[transform,box-shadow,border-color] duration-200 ease-out cursor-pointer"
                  style={{ animationDelay: `${Math.min(index, 15) * 24}ms` }}
                >
                  <div className="relative h-36 sm:h-44 md:h-48 bg-gray-50 flex items-center justify-center overflow-hidden">
                    {book.cover ? (
                      <img
                        src={book.cover}
                        alt={book.title}
                        className="h-full w-full object-contain bg-white transition-transform duration-300 ease-out group-hover:scale-[1.015]"
                        loading="lazy"
                        decoding="async"
                        draggable="false"
                      />
                    ) : (
                      <BookOpenIcon className="w-14 sm:w-20 h-14 sm:h-20 text-gray-300 group-hover:text-primary-300 transition-colors" />
                    )}

                    <button
                      type="button"
                      onClick={(event) => toggleFavorite(book.id, event)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 backdrop-blur-sm border border-gray-200 shadow-sm flex items-center justify-center transition-transform active:scale-90 z-20"
                      aria-label={`Remove ${book.title} from favorites`}
                      aria-pressed="true"
                    >
                      <HeartSolid className="w-5 h-5 text-red-500" />
                    </button>

                    {book.premium && !isPurchased && (
                      <div className="absolute inset-0 bg-black/20 flex items-center justify-center group-hover:bg-black/30 transition-colors">
                        <LockClosedIcon className="w-10 h-10 text-white drop-shadow-lg" />
                      </div>
                    )}
                  </div>

                  <div className="p-3 sm:p-4">
                    <h2
                      className="font-semibold text-primary-700 text-sm sm:text-lg truncate"
                      title={book.title}
                    >
                      {book.title}
                    </h2>
                    <div className="flex items-center gap-1 text-xs sm:text-sm text-gray-500 mt-1 min-w-0">
                      <UserIcon className="w-3.5 sm:w-4 h-3.5 sm:h-4 flex-none" />
                      <span className="truncate">
                        {book.author || 'Unknown author'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-2 mt-3">
                      <span className="text-[10px] sm:text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded truncate">
                        {book.sector || 'Book'}
                      </span>
                      {book.premium ? (
                        <span
                          className={`text-[10px] sm:text-xs px-2 py-0.5 rounded font-medium flex items-center gap-1 flex-none ${
                            isPurchased
                              ? 'bg-green-100 text-green-700'
                              : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {isPurchased ? (
                            <LockOpenIcon className="w-3 h-3" />
                          ) : (
                            <LockClosedIcon className="w-3 h-3" />
                          )}
                          {isPurchased ? 'Owned' : 'Premium'}
                        </span>
                      ) : (
                        <span className="text-[10px] sm:text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium flex-none">
                          Free
                        </span>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </main>

      {lockedBook && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4"
          onClick={() => setLockedBook(null)}
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl p-6 text-center shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
              <LockClosedIcon className="w-8 h-8 text-yellow-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-800">
              Premium book
            </h2>
            <p className="mt-2 text-sm text-gray-500">
              Purchase “{lockedBook.title}” from the Library before reading it.
            </p>
            <div className="mt-6 flex gap-3">
              <button
                type="button"
                onClick={() => setLockedBook(null)}
                className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-700 font-medium"
              >
                Close
              </button>
              <button
                type="button"
                onClick={() => window.location.assign('/library')}
                className="flex-1 py-3 rounded-xl bg-primary-600 text-white font-medium hover:bg-primary-700"
              >
                Open Library
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedBook && (
        <div className="favorite-reader-modal fixed inset-0 z-50 bg-black/90 flex flex-col">
          <header className="w-full bg-white flex items-center justify-between gap-3 px-3 sm:px-4 py-2 shadow-md flex-none">
            <h3 className="text-sm font-semibold text-primary-700 truncate min-w-0">
              {selectedBook.title}
            </h3>
            <div className="flex items-center gap-1 flex-none">
              <button
                type="button"
                onClick={(event) => toggleFavorite(selectedBook.id, event)}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Remove from favorites"
              >
                {favoriteIdSet.has(String(selectedBook.id)) ? (
                  <HeartSolid className="w-5 h-5 text-red-500" />
                ) : (
                  <HeartOutline className="w-5 h-5 text-gray-500" />
                )}
              </button>
              <button
                type="button"
                onClick={closeReader}
                className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
                aria-label="Close PDF reader"
              >
                <XMarkIcon className="w-6 h-6 text-gray-500" />
              </button>
            </div>
          </header>

          <div
            className="relative flex-1 min-h-0 w-full bg-white overflow-auto overscroll-contain"
            onContextMenu={(event) => event.preventDefault()}
          >
            {pdfLoading && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-20">
                <div className="w-3/4 max-w-md">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 transition-[width] duration-300 ease-out"
                      style={{ width: `${pdfProgress}%` }}
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-600 text-center tabular-nums">
                    {pdfProgress < 96
                      ? `Opening ${Math.round(pdfProgress)}%`
                      : 'Rendering PDF...'}
                  </p>
                </div>
              </div>
            )}

            {pdfError && !pdfLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white z-10 px-5">
                <div className="text-center max-w-sm">
                  <BookOpenIcon className="w-12 h-12 text-red-300 mx-auto mb-3" />
                  <p className="font-medium text-red-600">
                    Failed to open PDF
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    {pdfError}
                  </p>
                  <button
                    type="button"
                    onClick={() => openBook(selectedBook)}
                    className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700"
                  >
                    Try Again
                  </button>
                </div>
              </div>
            )}

            {selectedBook.pdfUrl && (
              <Worker workerUrl={PDF_WORKER_URL}>
                <Viewer
                  key={selectedBook.pdfUrl}
                  fileUrl={selectedBook.pdfUrl}
                  plugins={[defaultLayoutPluginInstance]}
                  defaultScale={1}
                  onDocumentLoad={handleDocumentLoad}
                  onDocumentError={handleDocumentError}
                />
              </Worker>
            )}
          </div>
        </div>
      )}
    </>
  );
}